/* eslint-disable no-param-reassign */
import { createStore, applyMiddleware, compose } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { responsiveStoreEnhancer } from 'redux-responsive';
import { fromJS } from 'immutable';
import { isEmpty } from 'lodash';
import type {
  CurrencyType,
  CurrencySymbols,
} from '@lt/components/utils/currency';

import { SEARCH_TYPE_NAMES } from 'src/constants/search';
import { getViewInitialState } from 'src/store/view/reducer';
import { MODULE_NAME } from 'src/constants';

import { isSSR } from 'src/helpers/ssr';
import { isDevelopment } from 'src/utils/commonUtils';
import { getExploreFiltersForQueryString } from 'src/utils/filtersUtils';
// import { getPreviousFitlers } from './middlewares/persistMiddleware';
import { searchParamsDTO } from 'src/store/enqueue/enqueueDTO';
import { buildHotel, buildMonths } from '../../utils/buildUtils';

import sagas from './sagas';
import reducer from './reducers';

import { prepareFilters } from './filters/reducer';

import type {
  INearestDates,
  INearestDepartures,
} from '../../presentation/EmptySearch/NearestOffers/types';
import type { IBreadCrumbs } from '../../data/models/header/breadcrumb.model';

const composeEnhancers =
  (!isSSR && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;
const sagaMiddleware = createSagaMiddleware();
const middlewares = [sagaMiddleware];

const enhancer = composeEnhancers(
  responsiveStoreEnhancer,
  applyMiddleware(...middlewares),
);

const DEFAULT_PAGE_LIMIT = 15;

// LT-25920: Explore: Доописать пропсы и стор
// LT-25921: Explore: Перенести стор с immutable

/* eslint-disable @typescript-eslint/no-explicit-any */
interface Props {
  logo: {
    src: string;
    srcset: string;
  };
  is_mobile: boolean;
  isWhitelabel: boolean;
  isWebview: boolean;
  isMetaregion: boolean;
  allowed_filters: any;
  search_params: Record<string, any>;
  hotels: Record<string, any>[];
  filtered_hotels: Record<string, any>[];
  hotels_count: number;
  page_limit: number;
  live_search_allowed: boolean;
  links: Record<string, any>;
  breadcrumbs: Array<IBreadCrumbs>;
  metadata: Record<string, any>;
  presenter_params: Record<string, any>;
  destination_bounds: any;
  fallback_search: boolean;
  nearest_departures: INearestDepartures;
  nearest_dates: INearestDates;
  availableSearchTypes: string | null;
  currency_symbol: CurrencySymbols;
  currency_code: CurrencyType;
  modifier?: {
    banner?: string;
    styles: {
      background_color?: string;
      text_color?: string;
      bold_text_color?: string;
      link_color?: string;
      highlited_block_color?: string;
    };
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export const getStore = (props: Props) => {
  const {
    logo,
    is_mobile: isMobile,
    isWebview,
    allowed_filters: filters,
    search_params,
    hotels,
    filtered_hotels,
    hotels_count,
    page_limit: pageLimit = DEFAULT_PAGE_LIMIT,
    live_search_allowed: liveSearchAllowed,
    links,
    breadcrumbs,
    metadata,
    presenter_params: presenterParams,
    destination_bounds,
    fallback_search,
    nearest_departures,
    nearest_dates,
    modifier,
    availableSearchTypes,
    currency_symbol: currencySymbol,
    currency_code: currencyCode,
    isMetaregion,
  } = props;

  if (isDevelopment() && !isSSR) {
    // eslint-disable-next-line no-console
    console.log('INITIAL_VALUES', props);
  }

  const validHotels = isEmpty(hotels) ? [] : hotels;
  const validFilters = isEmpty(filters)
    ? {}
    : { ...prepareFilters().toJS(), ...filters };

  const modifierStyles = modifier?.styles;

  const preloadedState = {
    logo,
    view: {
      app: MODULE_NAME.SEO,
      isMobile,
      isWebview,
      hideSidebar: true,
      isWidgetShown: true,
      showSubscribePopup: false,
      searchType: search_params.search_type,
      availableSearchTypes: availableSearchTypes || undefined,
      isMetaregion,
      isHotelOnly: search_params.search_type === SEARCH_TYPE_NAMES.HOTEL,
      currencySymbol,
      currencyCode,
      ...getViewInitialState(),
    },
    modifier: {
      banner: modifier?.banner,
      styles: {
        backgroundColor: modifierStyles?.background_color,
        textColor: modifierStyles?.text_color,
        boldTextColor: modifierStyles?.bold_text_color,
        linkColor: modifierStyles?.link_color,
        highlitedBlockColor: modifierStyles?.highlited_block_color,
      },
    },

    searcher: {
      adults: search_params.adults,
      kids: search_params.kids.ages,
      destination: search_params.destination,
      nights: search_params.duration,
      departure: {
        ...search_params.origin,
        iso2: search_params.origin.country_code,
      },
      startDate: new Date(search_params.start_date),
      flexDate: search_params.flex_dates,
      flexNights: search_params.flex_nights,
      presenterParams,
      destinationLoading: false,
      autocompleteQueryString: '',
      autocompleteOptions: [],
      searchUrl: search_params.query || '',
    },

    enqueue: {
      request_id: null,
      search_params: search_params ? searchParamsDTO(search_params) : null,
    },

    hotels: {
      hotels: validHotels.map((hotel) =>
        buildHotel(
          hotel,
          search_params,
          currencyCode,
          getExploreFiltersForQueryString(filters),
        ),
      ),
      currentPage: 0,
      hotelsCount: hotels_count,
      pageLimit,
      liveSearchAllowed,
      awaitingPagination: false,
      filtered_hotels,
      fallbackSearch: Boolean(fallback_search) && hotels?.length > 0,
      nearestDepartures: nearest_departures,
      nearestDates: nearest_dates,
    },

    map: {
      hotels: validHotels.reduce((obj, hotel) => {
        obj[hotel.hotel.id] = {
          long: hotel.hotel.long,
          lat: hotel.hotel.lat,
        };
        return obj;
      }, {}),
      hotelsInList: validHotels.map((hotel) => hotel.hotel.id),
      destinationBounds: destination_bounds,
    },

    linked: { ...links, linked_months: buildMonths(links.linked_months) },
    metadata: {
      ...metadata,
      breadcrumbs,
    },
    filters: validFilters,
  };

  if (isDevelopment() && !isSSR) {
    // eslint-disable-next-line no-console
    console.log('PRELOADED_STATE', preloadedState);
  }

  const store = createStore(reducer, fromJS(preloadedState), enhancer);
  sagaMiddleware.run(sagas);

  return store;
};
